import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { EmailVerifyBanner } from './EmailVerifyBanner';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Truck,
  Users,
  Receipt,
  FileText,
  Menu,
  X,
  LogOut,
  User,
  UserCircle,
  ChevronDown,
  Bell,
  Fuel,
  UserPlus,
  Shield,
  Store,
  Wrench,
  FileWarning,
  ShieldAlert,
  CheckCircle2,
  ClipboardCheck,
  Route,
  AlertTriangle,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';

// ---- Helpers (outside component for stable identity) ----

const getAlertIcon = (type) => {
  if (type === 'service') return <Wrench className="w-3.5 h-3.5 shrink-0" />;
  if (type === 'inspection') return <ClipboardCheck className="w-3.5 h-3.5 shrink-0" />;
  if (type === 'license' || type === 'driver_license') return <FileWarning className="w-3.5 h-3.5 shrink-0" />;
  return <ShieldAlert className="w-3.5 h-3.5 shrink-0" />;
};

const NAV_ITEMS = [
  { path: '/dashboard',  label: 'Dashboard',  icon: LayoutDashboard },
  { path: '/reports',    label: 'Reports',    icon: FileText },
  { path: '/expenses',   label: 'Expenses',   icon: Receipt },
  { path: '/vehicles',   label: 'Vehicles',   icon: Truck },
  { path: '/drivers',    label: 'Drivers',    icon: Users },
  { path: '/suppliers',  label: 'Suppliers',  icon: Store },
  { path: '/compliance', label: 'Compliance', icon: ClipboardCheck },
  { path: '/trips',      label: 'Trips',      icon: Route },
  { path: '/incidents',  label: 'Incidents',  icon: AlertTriangle },
];

const ACCESS_LABELS = {
  full_access: 'Full Access',
  view_entry: 'View + Entry',
  data_entry: 'Data Entry',
};

// ---- BellDropdown: simple ref-based dropdown (replaces Radix Popover to avoid portal issues) ----

const BellDropdown = ({ mobile, alerts, bellOpen, setBellOpen, onDismiss, onUpdateLicense, totalCount, errorCount }) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!bellOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setBellOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [bellOpen, setBellOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className={`relative flex items-center justify-center rounded-sm transition-colors ${
          mobile ? 'w-9 h-9 hover:bg-slate-100 text-slate-600' : 'w-9 h-9 hover:bg-white/10 text-slate-300'
        }`}
        data-testid="notification-bell"
        aria-label="Notifications"
        onClick={() => setBellOpen(prev => !prev)}
      >
        <Bell className="w-5 h-5" />
        {totalCount > 0 && (
          <span
            className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-none flex items-center justify-center text-[10px] font-bold text-white px-1 border border-slate-950 ${
              errorCount > 0 ? 'bg-rose-600' : 'bg-amber-500'
            }`}
            data-testid="alert-badge-count"
          >
            {totalCount > 9 ? '9+' : totalCount}
          </span>
        )}
      </button>
      {bellOpen && (
        <div
          className={`absolute ${mobile ? 'right-0' : 'left-0'} top-full mt-2 w-96 bg-white rounded-sm border border-slate-200 max-h-[480px] flex flex-col z-[200]`}
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-slate-700" />
              <span className="font-display font-bold text-sm uppercase tracking-wider text-slate-900">Alerts</span>
              {totalCount > 0 && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-none border ${errorCount > 0 ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                  {totalCount}
                </span>
              )}
            </div>
            <Link to="/dashboard" className="text-[11px] font-bold uppercase tracking-wider text-slate-900 hover:underline" onClick={() => setBellOpen(false)}>
              View all
            </Link>
          </div>
          <div className="overflow-y-auto flex-1">
            {alerts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10">
                <CheckCircle2 className="w-8 h-8 mb-2 text-emerald-500" />
                <p className="text-sm font-semibold text-slate-700">All clear</p>
                <p className="text-xs text-slate-400">No active alerts</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {alerts.map((alert, i) => {
                  const isError = alert.severity === 'error';
                  return (
                    <li
                      key={alert.alert_key || `bell-${i}`}
                      className={`flex items-start gap-3 px-4 py-3 ${isError ? 'bg-rose-50/40' : 'bg-amber-50/40'}`}
                      data-testid={`bell-alert-${i}`}
                    >
                      <span className={`mt-0.5 ${isError ? 'text-rose-600' : 'text-amber-600'}`}>
                        {getAlertIcon(alert.type)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs leading-snug ${isError ? 'text-rose-800' : 'text-amber-800'}`}>
                          {alert.message}
                        </p>
                        {alert.requires_new_date && alert.vehicle_id && (
                          <button
                            className="mt-1 text-[11px] font-bold uppercase tracking-wider text-rose-700 underline hover:no-underline"
                            onClick={() => onUpdateLicense(alert)}
                            data-testid={`bell-update-license-${i}`}
                          >
                            Update License Date
                          </button>
                        )}
                      </div>
                      {!alert.requires_new_date && alert.dismissable && (
                        <button
                          className={`shrink-0 rounded-none p-0.5 hover:bg-black/10 ${isError ? 'text-rose-500' : 'text-amber-500'}`}
                          onClick={() => onDismiss(alert.alert_key)}
                          data-testid={`bell-dismiss-${i}`}
                          title="Dismiss"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Main Layout ----

const DashboardLayout = () => {
  const { user, logout, isMainUser } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [selectedLicenseAlert, setSelectedLicenseAlert] = useState(null);
  const [newLicenseDate, setNewLicenseDate] = useState('');
  const [savingLicense, setSavingLicense] = useState(false);

  const PLATFORM_ADMIN_EMAIL = 'hpbza@icloud.com';
  const isPlatformAdmin = (user?.email || '').toLowerCase() === PLATFORM_ADMIN_EMAIL;
  const isImpersonating = typeof document !== 'undefined' && document.cookie.includes('impersonating=1');
  // "Pure" admin = platform admin viewing their own operator dashboard,
  // not currently impersonating a tenant. No fleet data belongs to them.
  const isPureAdmin = isPlatformAdmin && !isImpersonating;

  const fetchAlerts = useCallback(async () => {
    if (isPureAdmin) { setAlerts([]); return; }  // operator account has no fleet to alert on
    try {
      const stats = await api.getDashboardStats('month');
      setAlerts(stats.alerts || []);
    } catch {
      /* ignore */
    }
  }, [isPureAdmin]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  useEffect(() => {
    fetchAlerts();
  }, [location.pathname, fetchAlerts]);

  const handleDismissAlert = async (alertKey) => {
    try {
      await api.dismissAlert(alertKey);
      setAlerts(prev => prev.filter(a => a.alert_key !== alertKey));
    } catch {
      toast.error('Failed to dismiss alert');
    }
  };

  const handleOpenLicenseModal = (alert) => {
    setSelectedLicenseAlert(alert);
    setNewLicenseDate('');
    setBellOpen(false);
    setLicenseModalOpen(true);
  };

  const handleUpdateLicense = async () => {
    if (!newLicenseDate) { toast.error('Please enter the new license expiry date'); return; }
    setSavingLicense(true);
    try {
      await api.updateVehicleLicense(selectedLicenseAlert.vehicle_id, newLicenseDate);
      toast.success('License date updated successfully');
      setLicenseModalOpen(false);
      setSelectedLicenseAlert(null);
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update license date');
    } finally {
      setSavingLicense(false);
    }
  };

  const errorCount = alerts.filter(a => a.severity === 'error').length;
  const totalCount = alerts.length;

  const stopImpersonating = async () => {
    try {
      await api.stopImpersonating();
      toast.success('Returning to admin session…');
      window.location.href = '/subscribers';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not return to admin');
    }
  };

  const navItems = (() => {
    // Platform admin (when NOT impersonating) is a pure operator account —
    // no fleet nav, only Subscribers. While impersonating, show the tenant's
    // normal fleet nav so the admin can walk through their experience.
    if (isPlatformAdmin && !isImpersonating) {
      return [{ path: '/subscribers', label: 'Subscribers', icon: Shield }];
    }
    const base = isMainUser && isMainUser()
      ? [...NAV_ITEMS, { path: '/team', label: 'Team', icon: UserPlus }]
      : NAV_ITEMS;
    return isPlatformAdmin
      ? [...base, { path: '/subscribers', label: 'Subscribers', icon: Shield }]
      : base;
  })();

  const getAccessBadge = () => ACCESS_LABELS[user?.access_level] || user?.access_level;

  const bellProps = { alerts, bellOpen, setBellOpen, onDismiss: handleDismissAlert, onUpdateLicense: handleOpenLicenseModal, totalCount, errorCount };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Impersonation banner — pinned when platform admin is viewing a tenant */}
      {isImpersonating && (
        <div className="fixed top-0 inset-x-0 z-[60] bg-amber-500 text-slate-900 text-sm px-4 py-2 flex items-center justify-between shadow"
             data-testid="impersonation-banner">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            <span className="font-medium">
              Viewing <strong>{user?.email}</strong> as platform admin — any edits save against this tenant's data.
            </span>
          </div>
          <Button size="sm" variant="outline" className="bg-white"
                  onClick={stopImpersonating}
                  data-testid="stop-impersonating-btn">
            Return to admin
          </Button>
        </div>
      )}
      <div className={isImpersonating ? 'pt-10' : ''}>
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="flex items-center gap-2">
            <img
              src="/fleetcost-icon-192.png"
              alt="FleetCost.App"
              className="w-8 h-8 rounded-sm"
              data-testid="mobile-brand-icon"
            />
            <span className="font-display font-black text-lg tracking-tight text-slate-900" data-testid="mobile-brand-name">
              FleetCost<span className="text-blue-500">.App</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isPureAdmin && <BellDropdown mobile={true} {...bellProps} />}
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-72 sidebar-dark z-50
        transform transition-transform duration-200 ease-out
        lg:translate-x-0 flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-20 flex items-center justify-between gap-3 px-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-white rounded-sm flex items-center justify-center overflow-hidden">
              <img
                src="/fleetcost-icon-192.png"
                alt="FleetCost.App"
                className="w-9 h-9 object-contain"
                data-testid="sidebar-brand-icon"
              />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display font-black text-2xl text-white tracking-tight" data-testid="sidebar-brand-name">
                FleetCost<span className="text-blue-400">.App</span>
              </span>
              <span className="text-[11px] font-mono font-bold text-slate-400 tracking-[0.2em] uppercase mt-1">Fleet OS</span>
            </div>
          </div>
          {!isPureAdmin && <BellDropdown mobile={false} {...bellProps} />}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`sidebar-item ${isActive ? 'sidebar-item-active' : 'sidebar-item-inactive'}`}
              >
                <Icon className="w-[22px] h-[22px]" strokeWidth={isActive ? 2.5 : 2} />
                <span className="text-lg font-bold tracking-tight">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-full flex items-center gap-3 px-3 py-3 rounded-sm border-2 border-transparent hover:border-slate-700 hover:bg-white/[0.05] transition-colors"
                data-testid="user-menu-desktop"
              >
                <div className="w-10 h-10 bg-slate-800 rounded-sm flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="font-bold text-base text-white truncate tracking-tight">{user?.name}</p>
                  <p className="text-[11px] text-slate-300 truncate font-mono font-semibold uppercase tracking-wider">
                    {getAccessBadge() || user?.company_name || user?.email}
                  </p>
                </div>
                <ChevronDown className="w-5 h-5 text-slate-400 shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-semibold text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                {user?.role === 'sub_user' && (
                  <Badge variant="outline" className="mt-1.5">
                    <Shield className="w-3 h-3 mr-1" />
                    {getAccessBadge()}
                  </Badge>
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild data-testid="account-settings-menu">
                <Link to="/profile" className="cursor-pointer">
                  <UserCircle className="w-4 h-4 mr-2" />
                  Account Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 pt-14 lg:pt-0 min-h-screen">
        <EmailVerifyBanner />
        <Outlet />
      </main>

      {/* Update License Date Modal */}
      <Dialog open={licenseModalOpen} onOpenChange={setLicenseModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-slate-900">
              <FileWarning className="w-5 h-5 text-rose-600" />
              Update Vehicle License Date
            </DialogTitle>
            <DialogDescription>
              {selectedLicenseAlert?.vehicle_name
                ? `The license for "${selectedLicenseAlert.vehicle_name}" has expired. Enter the new expiry date to confirm renewal and clear this alert.`
                : 'Enter the new license expiry date to confirm renewal.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="layout_new_license_date" className="label-eyebrow">New License Expiry Date</Label>
              <Input
                id="layout_new_license_date"
                type="date"
                value={newLicenseDate}
                onChange={(e) => setNewLicenseDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="font-data"
                data-testid="layout-new-license-date-input"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setLicenseModalOpen(false)}>Cancel</Button>
              <Button
                onClick={handleUpdateLicense}
                disabled={savingLicense || !newLicenseDate}
                data-testid="layout-confirm-license-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {savingLicense ? 'Saving...' : 'Confirm New License'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default DashboardLayout;
